import { useMemo, useState } from 'react';
import { Icon } from '@iconify/react';

// Searchable multi-select over the society's flats, grouped by wing.
//
// Targeting a flat reaches everyone attached to it — owner and occupant alike —
// which is what "notify flat A-104" means in practice. Each row shows its
// owner/tenant counts so it's obvious before publishing whether a unit will
// actually reach anybody.
const FlatPicker = ({
    flats = [],
    selected = [],
    onChange,
    loading = false,
    error = null,
}) => {
    const [query, setQuery] = useState('');
    const [occupiedOnly, setOccupiedOnly] = useState(false);

    const selectedSet = useMemo(() => new Set(selected.map(Number)), [selected]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return flats.filter((f) => {
            if (occupiedOnly && !f.is_occupied) return false;
            if (!q) return true;
            return [f.label, f.flat_number, f.wing_title].filter(Boolean).join(' ').toLowerCase().includes(q);
        });
    }, [flats, query, occupiedOnly]);

    // Grouped by wing so picking "all of B Wing's ground floor" is a scan, not a
    // hunt through a flat alphabetical list.
    const grouped = useMemo(() => {
        const map = new Map();
        for (const f of filtered) {
            const key = f.wing_title || 'Unassigned';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(f);
        }
        return Array.from(map.entries());
    }, [filtered]);

    const toggle = (flatId) => {
        const id = Number(flatId);
        onChange(selectedSet.has(id)
            ? selected.filter((f) => Number(f) !== id)
            : [...selected, id]);
    };

    const toggleMany = (rows) => {
        const ids = rows.map((f) => Number(f.flat_id));
        const allOn = ids.every((id) => selectedSet.has(id));
        if (allOn) {
            const drop = new Set(ids);
            onChange(selected.filter((f) => !drop.has(Number(f))));
        } else {
            onChange([...new Set([...selected.map(Number), ...ids])]);
        }
    };

    const selectedFlats = useMemo(
        () => flats.filter((f) => selectedSet.has(Number(f.flat_id))),
        [flats, selectedSet]
    );

    // Publishing to only-empty units would reach nobody — worth saying up front
    // rather than after the fact via a target_count of 0.
    const emptySelected = selectedFlats.filter((f) => !f.is_occupied).length;

    return (
        <div className={`tw-border tw-rounded-md tw-overflow-hidden ${error ? 'tw-border-rose-400' : 'tw-border-gray-200'}`}>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-bg-gray-50 tw-border-b tw-border-gray-100">
                <div className="tw-relative tw-flex-1 tw-min-w-[180px]">
                    <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search by wing or flat number"
                        className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded focus:tw-outline-none focus:tw-border-sky-500"
                    />
                </div>
                <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-text-gray-600 tw-cursor-pointer">
                    <input type="checkbox" checked={occupiedOnly} onChange={(e) => setOccupiedOnly(e.target.checked)} />
                    Occupied only
                </label>
                <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600">
                    {selected.length} selected
                </span>
            </div>

            {selectedFlats.length > 0 && (
                <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100">
                    <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                        {selectedFlats.map((f) => (
                            <span
                                key={f.flat_id}
                                className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-[10px] tw-font-medium ${f.is_occupied ? 'tw-bg-sky-100 tw-text-sky-800' : 'tw-bg-gray-100 tw-text-gray-500'}`}
                            >
                                {f.label}
                                <button type="button" onClick={() => toggle(f.flat_id)} title={`Remove ${f.label}`}>
                                    <Icon icon="mdi:close" className="tw-text-xs" />
                                </button>
                            </span>
                        ))}
                    </div>
                    {emptySelected > 0 && (
                        <p className="tw-text-[10px] tw-text-amber-700 tw-mt-1.5 tw-flex tw-items-center tw-gap-1">
                            <Icon icon="mdi:alert-circle-outline" />
                            {emptySelected} of the selected flats has nobody registered — it will reach no one.
                        </p>
                    )}
                </div>
            )}

            <div className="tw-max-h-64 tw-overflow-y-auto">
                {loading && (
                    <div className="tw-flex tw-justify-center tw-py-6">
                        <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-gray-200 tw-border-t-sky-500" />
                    </div>
                )}
                {!loading && filtered.length === 0 && (
                    <p className="tw-text-[11px] tw-text-gray-400 tw-text-center tw-py-6">
                        {flats.length === 0 ? 'No flats found for this society' : 'No flats match that search'}
                    </p>
                )}
                {!loading && grouped.map(([wing, rows]) => (
                    <div key={wing}>
                        <div className="tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-1.5 tw-bg-gray-50 tw-border-y tw-border-gray-100 tw-sticky tw-top-0">
                            <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-gray-500">
                                {wing} <span className="tw-font-normal tw-text-gray-400">({rows.length})</span>
                            </span>
                            <button
                                type="button"
                                onClick={() => toggleMany(rows)}
                                className="tw-text-[10px] tw-font-medium tw-text-sky-700 hover:tw-text-sky-900"
                            >
                                {rows.every((f) => selectedSet.has(Number(f.flat_id))) ? 'Clear wing' : 'Select wing'}
                            </button>
                        </div>
                        {rows.map((f) => {
                            const id = Number(f.flat_id);
                            const active = selectedSet.has(id);
                            return (
                                <label
                                    key={id}
                                    className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-cursor-pointer tw-border-b tw-border-gray-50 ${active ? 'tw-bg-sky-50/60' : 'hover:tw-bg-gray-50'}`}
                                >
                                    <input type="checkbox" checked={active} onChange={() => toggle(id)} />
                                    <span className="tw-min-w-0 tw-flex-1">
                                        <span className="tw-block tw-text-xs tw-text-gray-800 tw-truncate">
                                            Flat {f.flat_number}
                                            {f.floor_number != null && (
                                                <span className="tw-text-gray-400"> · Floor {f.floor_number}</span>
                                            )}
                                        </span>
                                    </span>
                                    {f.is_occupied ? (
                                        <span className="tw-shrink-0 tw-text-[10px] tw-text-gray-500">
                                            {f.owner_count > 0 && `${f.owner_count} owner${f.owner_count === 1 ? '' : 's'}`}
                                            {f.owner_count > 0 && f.tenant_count > 0 && ' · '}
                                            {f.tenant_count > 0 && `${f.tenant_count} tenant${f.tenant_count === 1 ? '' : 's'}`}
                                        </span>
                                    ) : (
                                        <span className="tw-shrink-0 tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[9px] tw-font-semibold tw-bg-gray-100 tw-text-gray-400">
                                            Vacant
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default FlatPicker;
